<template>
  <div class="crud_form_wrapper payout_detail">
    <div class="form_title_wrapper d-flex align-center justify-space-between">
      <h4>{{ $t("SIDENAV.payouts.show") }}</h4>
      <div class="d-flex align-center">
        <v-btn
          v-if="$can('payouts cancel', 'payouts') && canCancel"
          color="error"
          class="me-2"
          :loading="isCancelling"
          :disabled="isCancelling"
          @click="dialogCancel = true"
        >
          {{ $t("payouts.cancelPayout") }}
        </v-btn>
        <v-btn @click="$router.go(-1)" style="color: var(--main_theme_clr)">
          <i class="fas fa-backward"></i>
        </v-btn>
      </div>
    </div>

    <div class="single_step_form_content_wrapper" v-if="data">
      <div class="summary_bar">
        <div>
          <span class="muted">{{ $t("payouts.fields.id") }}</span>
          <strong>#{{ data.id }}</strong>
        </div>
        <div>
          <span class="muted">{{ $t("payouts.purpose") }}</span>
          <strong>{{ purposeLabel(data.purpose) }}</strong>
        </div>
        <div v-if="data.booking_number || data.booking_id">
          <span class="muted">{{ $t("payouts.fields.booking_number") }}</span>
          <strong>{{ data.booking_number || data.booking_id }}</strong>
        </div>
        <div>
          <span class="muted">{{ $t("payouts.fields.user") }}</span>
          <strong>{{ userName }}</strong>
        </div>
        <span class="status_chip" :class="chipClass(data.status)">
          {{ statusLabel(data.status) }}
        </span>
      </div>

      <h5 class="section_title">{{ $t("payouts.amounts") }}</h5>
      <div class="amount_grid">
        <div class="amount_card" v-for="item in amountCards" :key="item.label">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </div>

      <h5 class="section_title">{{ $t("payouts.bankTransfer") }}</h5>
      <div class="facts">
        <div v-for="item in bankFacts" :key="item.label">
          <span class="muted">{{ item.label }}</span>
          <p>{{ item.value }}</p>
        </div>
      </div>

      <h5 class="section_title" v-if="dateFacts.length">{{ $t("payouts.dates") }}</h5>
      <div class="dates_row" v-if="dateFacts.length">
        <div v-for="item in dateFacts" :key="item.label">
          <span class="muted">{{ item.label }}</span>
          <p>{{ item.value }}</p>
        </div>
      </div>
    </div>
    <p v-else class="text-center py-5">{{ $t("TABLES.noData") }}</p>

    <v-dialog v-model="dialogCancel" max-width="500">
      <v-card>
        <v-card-title class="text-h5 justify-center">
          {{ $t("payouts.cancelConfirm") }}
        </v-card-title>
        <v-card-actions>
          <v-btn
            class="modal_confirm_btn"
            :loading="isCancelling"
            :disabled="isCancelling"
            @click="confirmCancel"
          >
            {{ $t("BUTTONS.ok") }}
          </v-btn>
          <v-btn
            class="modal_cancel_btn"
            :disabled="isCancelling"
            @click="dialogCancel = false"
          >
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import payoutLabels from "@/utils/payoutLabels";

export default {
  name: "ShowPayout",

  data() {
    return {
      isCancelling: false,
      dialogCancel: false,
      data: null,
    };
  },

  computed: {
    userName() {
      if (this.data?.user?.name) return this.data.user.name;
      return this.data?.user_id ? `#${this.data.user_id}` : "-";
    },
    canCancel() {
      return payoutLabels.canCancel(this.data);
    },
    amountCards() {
      if (!this.data) return [];
      return this.present([
        ["amount", "payouts.fields.amount"],
        ["transferred_amount", "payouts.fields.transferred_amount"],
        ["wallet_applied_amount", "payouts.fields.wallet_applied_amount"],
        ["order_total", "payouts.fields.order_total"],
        ["commission_total", "payouts.fields.commission_total"],
        ["net", "payouts.fields.net"],
        ["cash_amount", "payouts.fields.cash_amount"],
        ["hold_percent", "payouts.fields.hold_percent"],
        ["currency", "payouts.fields.currency"],
      ]);
    },
    bankFacts() {
      if (!this.data) return [];
      const facts = [
        ["part", "payouts.part", this.partLabel(this.data.part)],
        ["applied_reason", "payouts.fields.applied_reason", this.reasonLabel(this.data.applied_reason)],
        ["attempts", "payouts.fields.attempts"],
        ["payoutId", "payouts.fields.payoutId"],
        ["payoutReference", "payouts.fields.payoutReference"],
        ["bulkId", "payouts.fields.bulkId"],
        ["iban", "payouts.fields.iban"],
        ["bicCode", "payouts.fields.bicCode"],
        ["transferMode", "payouts.fields.transferMode"],
        ["responseCode", "payouts.fields.responseCode"],
        ["responseMessage", "payouts.fields.responseMessage"],
        ["description", "payouts.fields.description"],
        ["wallet_settlement_request_id", "payouts.fields.wallet_settlement_request_id"],
      ];
      return facts
        .map(([key, label, display]) => ({
          label: this.$t(label),
          value: display !== undefined ? display : this.fieldValue(key, this.data[key]),
          raw: this.data[key],
        }))
        .filter((item) => item.raw !== null && item.raw !== undefined && item.raw !== "");
    },
    dateFacts() {
      if (!this.data) return [];
      return this.present([
        ["finished_at", "payouts.fields.finished_at"],
        ["available_at", "payouts.fields.available_at"],
        ["auto_release_at", "payouts.fields.auto_release_at"],
        ["released_at", "payouts.fields.released_at"],
      ]);
    },
  },

  methods: {
    text(value) {
      return value === null || value === undefined || value === "" ? "-" : String(value);
    },
    present(pairs) {
      return pairs
        .filter(([key]) => {
          const value = this.data[key];
          return value !== null && value !== undefined && value !== "";
        })
        .map(([key, label]) => ({
          label: this.$t(label),
          value: this.fieldValue(key, this.data[key]),
        }));
    },
    purposeLabel(value) {
      return payoutLabels.purpose(this, value);
    },
    statusLabel(value) {
      return payoutLabels.status(this, value);
    },
    partLabel(value) {
      return payoutLabels.part(this, value);
    },
    reasonLabel(value) {
      return payoutLabels.reason(this, value);
    },
    fieldValue(key, value) {
      return payoutLabels.fieldValue(this, key, value);
    },
    chipClass(status) {
      return {
        held: status === "held_complaint" || status === "failed",
        wallet: status === "applied_to_wallet" || status === "processing" || status === "scheduled",
        done: status === "transferred",
      };
    },
    async showPayout() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `payouts/${this.$route.params?.id}`,
        });
        const payload = res.data.data || {};
        this.data = payload.payout || payload.Payout || payload;
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    async confirmCancel() {
      this.isCancelling = true;
      try {
        await this.$axios({
          method: "POST",
          url: `payouts/${this.$route.params?.id}/cancel`,
        });
        this.dialogCancel = false;
        this.$message.success(this.$t("payouts.cancelSuccess"));
        this.showPayout();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.isCancelling = false;
      }
    },
  },

  created() {
    this.showPayout();
  },
};
</script>

<style scoped lang="scss">
.payout_detail {
  .summary_bar,
  .dates_row,
  .facts {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    align-items: flex-start;
    margin-bottom: 24px;
  }

  .muted {
    display: block;
    color: #7a7a7a;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .section_title {
    margin: 8px 0 16px;
    font-size: 16px;
    font-weight: 700;
  }

  .amount_grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 28px;
  }

  .amount_card {
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 14px 16px;
    background: #fafafa;

    span {
      display: block;
      color: #7a7a7a;
      font-size: 13px;
      margin-bottom: 6px;
    }

    strong {
      font-size: 18px;
    }
  }

  .status_chip {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 999px;
    background: #eee;
    font-weight: 600;
    font-size: 13px;

    &.held {
      background: #fdecea;
      color: #c62828;
    }

    &.wallet {
      background: #fff4e5;
      color: #ef6c00;
    }

    &.done {
      background: #e8f5e9;
      color: #2e7d32;
    }
  }
}
</style>
